import './SearchModeControl.scss';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataTable, { IDataTableHandle } from '@srm/ui/data-table';
import { AddressBookService } from '@services/AddressBookService';
import { useDxloSource } from '@srm/ui/data-table/dxlo';
import { IAbookTab } from '@models/Forms/IForms';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { IAbookSettings } from '@atoms/Abookpicker/AbookpickerModal/IAbookSettings';
import Input from '@srm/ui/input';
import { buildAbookColumns } from './abookGridColumns';

export interface ISearchModeControlProp extends Omit<IAbookSettings, 'showChips'> {
    currentTab: IAbookTab;
    searchApiUrl: string;
    onSelectedRowItems: (value: IAddressBookData[]) => void;
    onSelectedKeys?: (value: number[]) => void;
    getParams: (options: unknown) => string;
}

/** Пустой поиск бэкенд ждёт литералом «null» в пути запроса. */
const EMPTY_SEARCH = 'null';

/**
 * Поиск по внешнему источнику адресной книги.
 *
 * Строку поиска обрабатывает сервер вместе со страницей: новый текст (с дебаунсом)
 * или другая вкладка — другая выборка, список начинается заново.
 */
const SearchModeControl: FC<ISearchModeControlProp> = (p: ISearchModeControlProp) => {
    const tableRef = useRef<IDataTableHandle<IAddressBookData>>(null);
    // Настройки приходят новым объектом на каждый рендер модалки, а загрузчик таблицы меняется только вместе
    // с выборкой (его смена — новый список с начала), поэтому настройки читаются через ref.
    const settingsRef = useRef(p);
    settingsRef.current = p;
    const [searchText, setSearchText] = useState<string>('');
    const initialSelectionRef = useRef(false);

    /** Уже выбранные записи отмечаются один раз — после первой порции строк. */
    const markInitialSelection = useCallback(() => {
        const s = settingsRef.current;
        if (initialSelectionRef.current || !s.selected || s.selected.length === 0) return;
        initialSelectionRef.current = true;
        tableRef.current?.selectRows(s.selected.map(String));
    }, []);

    // вкладка задаёт кэш поиска на бэкенде: её смена, как и новый текст, — другая выборка
    const tabCache = p.currentTab.cache;

    const source = useDxloSource<IAddressBookData>({
        params: [searchText, tabCache],
        fetch: async (options, signal) => {
            const s = settingsRef.current;
            const response = await AddressBookService.fetchExternalSearch(
                s.searchApiUrl,
                searchText === '' ? EMPTY_SEARCH : searchText,
                s.getParams(options),
                signal,
            );
            markInitialSelection();
            return response;
        },
    });

    // другая вкладка — стартовый выбор ставится заново
    const prevTabRef = useRef(tabCache);
    useEffect(() => {
        if (prevTabRef.current === tabCache) return;
        prevTabRef.current = tabCache;
        initialSelectionRef.current = false;
    }, [tabCache]);

    const columns = useMemo(() => buildAbookColumns(p.currentTab.columns), [p.currentTab.columns]);

    const onSelectionChange = useCallback((_keys: string[], rows: IAddressBookData[]) => {
        const s = settingsRef.current;
        // бэкенд отдаёт `key` строкой — наружу контракт числовой
        s.onSelectedKeys?.(rows.map((row) => Number(row.key)));
        s.onSelectedRowItems(rows);
    }, []);

    return (
        <div className="abook-container-search">
            <div className="abook-container-search-input">
                <Input placeholder="Введите ФИО для поиска" debounce={150} onDebouncedChange={setSearchText} />
            </div>
            <div className="abook-container-search__grid">
                <DataTable<IAddressBookData>
                    controlRef={tableRef}
                    columns={columns}
                    getRowId={(row) => String(row.key)}
                    source={source}
                    // список идёт сплошным: строки догружаются прокруткой, а под таблицей
                    // висит счётчик «Загружено X из Y»
                    serverScrollMode="infinite"
                    paging={false}
                    selectable
                    singleSelection={!p.isMultiple}
                    selectOnRowClick
                    onSelectionChange={onSelectionChange}
                    defaultWrapMode={p.wordWrapEnabled ? 'wrap' : 'nowrap'}
                    // высота — от зоны .abook-grid, см. ListModeControl
                    fillHeight
                    fitColumns
                    overflowTooltip
                    emptyText="Нет строк"
                />
            </div>
        </div>
    );
};

export default SearchModeControl;
